/* eslint-disable react-hooks/rules-of-hooks -- Gli hook del componente sono dichiarati incondizionatamente; il rule analyzer segnala un falso positivo storico. */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import apiUrl from "../../Apiurl";
import stilePulsanti from "../../interfaccia/stilePulsanti";
import ImmaginiArticolo from "./ImmaginiArticolo";
import LibreriaImmaginiProdotto from "./LibreriaImmaginiProdotto";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";

const sanitizeSegment = (value, preserveCase = false) => {
  if (!value) {
    return "";
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return "";
  }
  const cleaned = trimmed
    .replace(/\s+/g, "-")
    .replace(/[^A-Za-z0-9_-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return preserveCase ? cleaned : cleaned.toLowerCase();
};

const buildTargetFileName = (fileName, index, codiceVS) => {
  const codiceSegment = sanitizeSegment(codiceVS, true);
  const baseName = codiceSegment || "immagine";
  const sequence = String(index + 1);
  const extensionMatch = fileName.match(/(\.[A-Za-z0-9]+)$/);
  const extension = extensionMatch ? extensionMatch[1].toLowerCase() : "";
  return `${baseName}-${sequence}${extension}`;
};

const MAX_FILES = 20;
const JOB_POLL_INTERVAL_MS = 3000;
const JOB_ACTIVE_STATUSES = new Set(["PENDING", "APPROVED", "IN_PROGRESS"]);
const JOB_REVIEW_STATUS = "READY_FOR_REVIEW";
const JOB_TERMINAL_STATUSES = new Set([
  "REJECTED",
  "ERROR",
  "SUCCESS",
]);
const JOB_STATUS_BADGES = {
  PENDING: "mid-gray",
  IN_PROGRESS: "blue",
  READY_FOR_REVIEW: "dark-blue",
  APPROVED: "blue",
  REJECTED: "dark-red",
  ERROR: "dark-red",
  SUCCESS: "dark-green",
};

const getStatusClassName = (status) => JOB_STATUS_BADGES[status] ?? "gray";

const getJobSiteOutcomes = (job) => {
  const sites = job?.metadata?.sites;
  return Array.isArray(sites)
    ? sites.filter(
        (site) =>
          site &&
          (site.site === "VIG" || site.site === "BOT") &&
          (site.status === "PENDING" ||
            site.status === "IN_PROGRESS" ||
            site.status === "SUCCESS" ||
            site.status === "ERROR" ||
            site.status === "SKIPPED")
      )
    : [];
};

const JOB_TIMELINE_STEPS = [
  { key: "upload", label: "Carico le immagini..." },
  { key: "validation", label: "Controllo che non ci siano problemi..." },
  { key: "processing", label: "Modifico dimensioni e proporzioni..." },
  { key: "review", label: "Aspetto che le approvi..." },
  { key: "archive", label: "Archivio la revisione immagini..." },
  { key: "approval", label: "Dò il via ai caricamenti..." },
  { key: "prestashop_bottero", label: "Carico su Prestashop Bottero..." },
  { key: "prestashop_viglietti", label: "Carico su Prestashop Viglietti..." },
  { key: "connector", label: "Copio in cartella di Moda..." },
  { key: "cleanup", label: "Sparecchio e pulisco..." },
];

const LAST_STEP_PATTERNS = [
  { regex: /(upload|caricamento|accodato|inviato)/i, step: "upload" },
  {
    regex: /(validazione|preparazione|analisi|queue|pending)/i,
    step: "validation",
  },
  {
    regex: /(rielaboraz|elaboraz|ridimension|processing|convert)/i,
    step: "processing",
  },
  { regex: /(revisione|review|anteprima)/i, step: "review" },
  { regex: /(archiv|r2)/i, step: "archive" },
  { regex: /(approv|sftp|consegna)/i, step: "approval" },
  { regex: /(bottero)/i, step: "prestashop_bottero" },
  { regex: /(viglietti)/i, step: "prestashop_viglietti" },
  { regex: /(tembo)/i, step: "prestashop_viglietti" },
  { regex: /(prestashop|connector|sync)/i, step: "connector" },
  { regex: /(cleanup|pulizia|rimozion|cartella)/i, step: "cleanup" },
];

const STAGE_TO_TIMELINE_STEP = {
  upload: "upload",
  validation: "validation",
  preprocessing: "processing",
  processing: "processing",
  ready_for_review: "review",
  review: "review",
  archive: "archive",
  r2: "archive",
  approved: "approval",
  approval: "approval",
  uploaded_to_sftp: "approval",
  tembo: "prestashop_viglietti",
  prestashop_bottero: "prestashop_bottero",
  prestashop_viglietti: "prestashop_viglietti",
  connector: "connector",
  prestashop_connector_triggered: "connector",
  prestashop_connector_error: "connector",
  connector_copy: "connector",
  connector_copy_error: "connector",
  cleanup: "cleanup",
  cleanup_error: "cleanup",
};

const TIMELINE_STATE_LABELS = {
  done: "completato",
  current: "in corso",
  pending: "in attesa",
  error: "errore",
  skipped: "saltato",
};

const mapStageToTimelineStep = (stage) => {
  if (!stage) {
    return null;
  }
  const normalized = String(stage).toLowerCase();
  if (STAGE_TO_TIMELINE_STEP[normalized]) {
    return STAGE_TO_TIMELINE_STEP[normalized];
  }
  if (normalized.includes("sftp")) {
    return "approval";
  }
  if (normalized.includes("upload")) {
    return "upload";
  }
  if (normalized.includes("valid")) {
    return "validation";
  }
  if (
    normalized.includes("elabor") ||
    normalized.includes("process") ||
    normalized.includes("resize")
  ) {
    return "processing";
  }
  if (normalized.includes("review")) {
    return "review";
  }
  if (normalized.includes("approv") || normalized.includes("sftp")) {
    return "approval";
  }
  if (normalized.includes("tembo")) {
    return "tembo";
  }
  if (normalized.includes("prestashop") || normalized.includes("connector")) {
    return "connector";
  }
  if (normalized.includes("cleanup") || normalized.includes("pulizia")) {
    return "cleanup";
  }
  return null;
};

const resolveJobTimeline = (job) => {
  if (!job) {
    return JOB_TIMELINE_STEPS.map((step, index) => ({
      ...step,
      state: index === 0 ? "current" : "pending",
    }));
  }

  const status = job.status ?? "";
  const lastStepRaw = job.lastStep ?? "";
  const lastStepMatch =
    LAST_STEP_PATTERNS.find((pattern) => pattern.regex.test(lastStepRaw)) ??
    null;
  const metadataStage =
    mapStageToTimelineStep(
      job.metadata?.stage ??
        job.metadata?.lastStage ??
        job.metadata?.error?.stage ??
        ""
    ) ?? null;
  const currentKey =
    metadataStage ??
    (lastStepMatch ? lastStepMatch.step : null) ??
    (status === "PENDING"
      ? "validation"
      : status === "IN_PROGRESS"
      ? "processing"
      : status === JOB_REVIEW_STATUS
      ? "review"
      : status === "APPROVED"
      ? "approval"
      : status === "REJECTED"
      ? "review"
      : status === "SUCCESS"
      ? "cleanup"
      : "upload");

  const errorKey =
    status === "ERROR"
      ? metadataStage ??
        (lastStepMatch ? lastStepMatch.step : null) ??
        mapStageToTimelineStep(
          job.metadata?.error?.stage ?? job.metadata?.stage ?? ""
        ) ??
        "processing"
      : status === "REJECTED"
      ? "review"
      : null;

  const siteOutcomesBySite = getJobSiteOutcomes(job).reduce((result, site) => {
    result[site.site] = site;
    return result;
  }, {});
  const isSuccess = status === "SUCCESS";
  let currentSeen = false;

  return JOB_TIMELINE_STEPS.map((step) => {
    const site =
      step.key === "prestashop_viglietti"
        ? "VIG"
        : step.key === "prestashop_bottero"
        ? "BOT"
        : null;
    const siteOutcome = site ? siteOutcomesBySite[site] : null;
    if (siteOutcome) {
      const siteState =
        siteOutcome.status === "SUCCESS"
          ? "done"
          : siteOutcome.status === "ERROR"
          ? "error"
          : siteOutcome.status === "SKIPPED"
          ? "skipped"
          : siteOutcome.status === "IN_PROGRESS"
          ? "current"
          : "pending";
      if (siteState === "current" || siteState === "error") {
        currentSeen = true;
      }
      return {
        ...step,
        state: siteState,
        detail: siteOutcome.message,
      };
    }

    let state = "pending";
    if (isSuccess) {
      state = "done";
    } else if (errorKey && step.key === errorKey) {
      state = "error";
      currentSeen = true;
    } else if (!currentSeen) {
      if (step.key === currentKey) {
        state =
          JOB_TERMINAL_STATUSES.has(status) &&
          status !== "ERROR" &&
          status !== "REJECTED"
            ? "done"
            : "current";
        currentSeen = true;
      } else {
        state = "done";
      }
    } else {
      state = "pending";
    }

    return {
      ...step,
      state,
    };
  });
};

const getTimelineDotClasses = (state) => {
  switch (state) {
    case "done":
      return "br-100 h1 w1 ba b--dark-green bg-dark-green";
    case "current":
      return "br-100 h1 w1 ba b--blue bg-blue";
    case "error":
      return "br-100 h1 w1 ba b--dark-red bg-dark-red";
    case "skipped":
      return "br-100 h1 w1 ba b--dark-yellow bg-yellow";
    default:
      return "br-100 h1 w1 ba b--moon-gray bg-white";
  }
};

const getTimelineLabelClasses = (state) => {
  switch (state) {
    case "done":
      return "dark-green";
    case "current":
      return "blue";
    case "error":
      return "dark-red";
    case "skipped":
      return "dark-yellow";
    default:
      return "gray";
  }
};

const getTimelineBadgeClasses = (state) => {
  switch (state) {
    case "done":
      return "ph2 pv1 br-pill bg-washed-green dark-green";
    case "current":
      return "ph2 pv1 br-pill bg-washed-blue blue";
    case "error":
      return "ph2 pv1 br-pill bg-washed-red dark-red";
    case "skipped":
      return "ph2 pv1 br-pill bg-washed-yellow dark-yellow";
    default:
      return "ph2 pv1 br-pill bg-near-white gray";
  }
};

const getTimelineConnectorClasses = (state) => {
  switch (state) {
    case "done":
      return "bg-dark-green";
    case "current":
      return "bg-blue";
    case "error":
      return "bg-dark-red";
    default:
      return "bg-light-silver";
  }
};

function UploadImmaginiArticolo(props) {
  const safeProps = props ?? {};
  const {
    product,
    token,
    uploadedBy,
    onCompleted,
    brandCode,
    canViewDebugDetails,
    botProdIDps,
    refreshKey,
  } = safeProps;
  const [brandOverride, setBrandOverride] = useState("");
  const [items, setItems] = useState([]);
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [jobPollError, setJobPollError] = useState("");
  const [jobPreviews, setJobPreviews] = useState([]);
  const [jobActionStatus, setJobActionStatus] = useState("idle");
  const [jobActionError, setJobActionError] = useState("");
  const [loadedPreviewKey, setLoadedPreviewKey] = useState(null);
  const fileInputRef = useRef(null);
  const jobPreviewsRef = useRef([]);
  const [missingImageStatus, setMissingImageStatus] = useState("idle");
  const [missingImageMessage, setMissingImageMessage] = useState("");
  const [existingImageStatus, setExistingImageStatus] = useState("idle");
  const [existingImageMessage, setExistingImageMessage] = useState("");
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );
  const revokePreviewUrls = useCallback((previews) => {
    if (!Array.isArray(previews)) {
      return;
    }
    previews.forEach((item) => {
      if (item && item.url) {
        URL.revokeObjectURL(item.url);
      }
    });
  }, []);
  const { buttonClassesWhite, buttonClassesGreen, buttonClassesRed } =
    stilePulsanti;
  const codiceVS = product?.codiceVS ?? "";
  const codicePS = product?.codicePS ?? "";
  const overrideBrandTrimmed = brandOverride.trim();
  const baseBrandCode =
    typeof brandCode === "string"
      ? brandCode.trim()
      : brandCode
      ? String(brandCode).trim()
      : "";
  const modaBrandCode = overrideBrandTrimmed || baseBrandCode || "";
  const jobStatus = activeJob?.status ?? null;
  const jobIsTerminal = jobStatus
    ? JOB_TERMINAL_STATUSES.has(jobStatus)
    : false;
  const canStartNewUpload = !activeJob || jobIsTerminal;
  const jobIsActive = Boolean(activeJob) && !jobIsTerminal;
  const jobAwaitingReview = jobStatus === JOB_REVIEW_STATUS;
  const showDebugDetails = Boolean(canViewDebugDetails);
  const jobProgressPercent =
    activeJob && activeJob.totalFiles
      ? Math.round(
          (Math.min(activeJob.processedFiles || 0, activeJob.totalFiles) /
            activeJob.totalFiles) *
            100
        )
      : 0;
  const progressBarClass =
    activeJob?.status === "ERROR"
      ? "bg-dark-red"
      : activeJob?.status === "SUCCESS"
      ? "bg-dark-green"
      : "bg-blue";
  const jobDisableUploadsReason = jobIsActive
    ? jobAwaitingReview
      ? "Completa la revisione delle immagini prima di caricare nuove foto."
      : "Attendere la conclusione dell'elaborazione immagini prima di un nuovo upload."
    : "";
  const numberFormatter = useMemo(() => new Intl.NumberFormat("it-IT"), []);
  const jobTimeline = useMemo(() => resolveJobTimeline(activeJob), [activeJob]);
  const jobErrorTooltip = useMemo(() => {
    if (!activeJob) {
      return null;
    }
    const status = activeJob.status ?? "";
    if (status !== "ERROR" && status !== "REJECTED") {
      return null;
    }
    const metadataError = activeJob.metadata?.error;
    if (typeof metadataError === "string" && metadataError.trim()) {
      return metadataError.trim();
    }
    if (
      metadataError &&
      typeof metadataError === "object" &&
      typeof metadataError.message === "string" &&
      metadataError.message.trim()
    ) {
      return metadataError.message.trim();
    }
    if (typeof activeJob.message === "string" && activeJob.message.trim()) {
      return activeJob.message.trim();
    }
    return status === "REJECTED"
      ? "Job rifiutato."
      : "Errore durante questa fase.";
  }, [activeJob]);
  const jobActionInProgress = jobActionStatus !== "idle";
  const metadataKey =
    activeJob && activeJob.status === JOB_REVIEW_STATUS && activeJob.metadata
      ? `${activeJob.jobId}:${
          activeJob.metadata?.createdAt ?? activeJob.updatedAt ?? ""
        }:${activeJob.metadata?.files?.length ?? 0}`
      : null;
  const metadataFilesCount = Array.isArray(activeJob?.metadata?.files)
    ? activeJob.metadata.files.length
    : 0;

  useEffect(() => {
    return () => {
      items.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
  }, [items]);

  useEffect(() => {
    setBrandOverride("");
  }, [baseBrandCode, codiceVS]);

  useEffect(() => {
    setActiveJob(null);
    setJobPollError("");
    setJobActionError("");
    setLoadedPreviewKey(null);
    setJobPreviews((prev) => {
      revokePreviewUrls(prev);
      return [];
    });
    jobPreviewsRef.current = [];
    setSuccessMessage("");
    setErrorMessage("");
  }, [codiceVS, revokePreviewUrls]);

  useEffect(() => {
    setMissingImageStatus("idle");
    setMissingImageMessage("");
  }, [codiceVS]);

  useEffect(() => {
    return () => {
      revokePreviewUrls(jobPreviewsRef.current);
    };
  }, [revokePreviewUrls]);

  useEffect(() => {
    setItems((prev) =>
      prev.map((item, index) => ({
        ...item,
        targetName: buildTargetFileName(item.originalName, index, codiceVS),
      }))
    );
  }, [codiceVS]);

  const clearItems = useCallback(() => {
    setItems((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
    setProgress(0);
  }, []);

  const resetJobState = useCallback(() => {
    setActiveJob(null);
    setJobPollError("");
    setJobActionError("");
    setLoadedPreviewKey(null);
    setJobPreviews((prev) => {
      revokePreviewUrls(prev);
      return [];
    });
    jobPreviewsRef.current = [];
    setUploadStatus("idle");
    setProgress(0);
    setSuccessMessage("");
    clearItems();
  }, [revokePreviewUrls, clearItems]);

  const appendFiles = useCallback(
    (incomingFiles) => {
      if (!incomingFiles.length) {
        return false;
      }

      const acceptedByType = [];
      const rejectedByType = [];

      incomingFiles.forEach((file) => {
        const isValidType =
          /image\/(jpeg|jpg|png)/i.test(file.type) ||
          /\.(jpe?g|png)$/i.test(file.name);
        if (isValidType) {
          acceptedByType.push(file);
        } else {
          rejectedByType.push(file.name);
        }
      });

      if (!acceptedByType.length) {
        if (rejectedByType.length) {
          setErrorMessage(
            `Formati non supportati: ${rejectedByType.join(
              ", "
            )} (solo JPG/JPEG/PNG).`
          );
        }
        return false;
      }

      let addedCount = 0;
      let skippedForLimit = 0;
      setItems((prev) => {
        const remainingSlots = MAX_FILES - prev.length;
        if (remainingSlots <= 0) {
          skippedForLimit = acceptedByType.length;
          return prev;
        }
        const usableFiles = acceptedByType.slice(0, remainingSlots);
        addedCount = usableFiles.length;
        skippedForLimit = acceptedByType.length - usableFiles.length;
        const startIndex = prev.length;
        const createdAt = Date.now();
        const newEntries = usableFiles.map((file, index) => {
          const targetName = buildTargetFileName(
            file.name,
            startIndex + index,
            codiceVS
          );
          return {
            id: `${createdAt}-${index}`,
            originalName: file.name,
            file,
            targetName,
            previewUrl: URL.createObjectURL(file),
          };
        });
        return [...prev, ...newEntries];
      });

      const messages = [];
      if (rejectedByType.length) {
        messages.push(
          `Formati non supportati: ${rejectedByType.join(
            ", "
          )} (solo JPG/JPEG/PNG).`
        );
      }
      if (skippedForLimit > 0) {
        messages.push(
          `Puoi caricare al massimo ${MAX_FILES} immagini per volta. Ignorati ${skippedForLimit} file oltre il limite.`
        );
      }
      if (messages.length) {
        setErrorMessage(messages.join(" "));
      } else {
        setErrorMessage("");
      }

      if (addedCount > 0) {
        setUploadStatus("idle");
        setSuccessMessage(
          `${addedCount} ${
            addedCount === 1 ? "immagine aggiunta" : "immagini aggiunte"
          }.`
        );
        setProgress(0);
      } else {
        setSuccessMessage("");
      }

      return addedCount > 0;
    },
    [codiceVS]
  );

  const handleFileSelection = useCallback(
    (event) => {
      const files = Array.from(event.target.files || []);
      appendFiles(files);
      event.target.value = "";
    },
    [appendFiles]
  );

  const handleRemoveItem = useCallback(
    (id) => {
      setItems((prev) => {
        const next = prev.filter((item) => item.id !== id);
        const removed = prev.find((item) => item.id === id);
        if (removed) {
          URL.revokeObjectURL(removed.previewUrl);
        }
        return next.map((item, index) => ({
          ...item,
          targetName: buildTargetFileName(item.originalName, index, codiceVS),
        }));
      });
      setUploadStatus("idle");
      setErrorMessage("");
      setSuccessMessage("");
      setProgress(0);
    },
    [codiceVS]
  );

  const handleClearSelection = useCallback(() => {
    clearItems();
    setUploadStatus("idle");
    setErrorMessage("");
    setSuccessMessage("");
  }, [clearItems]);

  const hasValidContext = useMemo(
    () => Boolean(codiceVS && token && modaBrandCode && canStartNewUpload),
    [codiceVS, token, modaBrandCode, canStartNewUpload]
  );
  const hasItemsSelected = items.length > 0;
  const canSelectImages = hasValidContext && !jobIsActive;
  const selectButtonClass = [
    hasItemsSelected ? buttonClassesWhite : buttonClassesGreen,
    canSelectImages ? "" : "o-50 not-allowed pointer-events-none",
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const canUploadImages =
    hasItemsSelected && hasValidContext && uploadStatus !== "uploading";
  const uploadButtonClass = [
    canUploadImages ? buttonClassesGreen : buttonClassesWhite,
    canUploadImages ? "" : "o-50 not-allowed pointer-events-none",
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const handleDragOver = useCallback(
    (event) => {
      event.preventDefault();
      if (!hasValidContext) {
        return;
      }
      setIsDragActive(true);
    },
    [hasValidContext]
  );

  const handleDragLeave = useCallback((event) => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      setIsDragActive(false);
      if (!hasValidContext) {
        return;
      }
      const files = Array.from(event.dataTransfer?.files || []);
      appendFiles(files);
    },
    [appendFiles, hasValidContext]
  );

  const triggerFileDialog = useCallback(() => {
    if (!hasValidContext) {
      return;
    }
    fileInputRef.current?.click();
  }, [hasValidContext]);

  const fetchJobStatus = useCallback(
    async (jobId) => {
      if (!jobId) {
        throw new Error("Job ID mancante");
      }
      const url = `${apiUrl}/moda/articoli/${encodeURIComponent(
        codiceVS
      )}/immagini/${jobId}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });
      const payload =
        response?.data?.job ?? response?.data?.data ?? response?.data;
      if (!payload) {
        throw new Error("Risposta job non valida");
      }
      let metadata = payload.metadata ?? payload.metadata_payload ?? null;
      if (metadata && typeof metadata === "string") {
        try {
          metadata = JSON.parse(metadata);
        } catch {
          metadata = null;
        }
      }
      return {
        jobId: payload.jobId ?? payload.job_id ?? jobId,
        productCode: payload.productCode ?? payload.product_code ?? codiceVS,
        brandCode:
          payload.brandCode ??
          payload.brand_code ??
          payload.brandId ??
          payload.brand_id ??
          null,
        status: payload.status ?? payload.jobStatus ?? "UNKNOWN",
        totalFiles: Number(payload.totalFiles ?? payload.total_files ?? 0),
        processedFiles: Number(
          payload.processedFiles ?? payload.processed_files ?? 0
        ),
        lastStep: payload.lastStep ?? payload.last_step ?? null,
        message: payload.message ?? null,
        createdAt: payload.createdAt ?? payload.created_at ?? null,
        updatedAt: payload.updatedAt ?? payload.updated_at ?? null,
        metadata,
      };
    },
    [apiUrl, codiceVS, token]
  );

  const updateJobFromServer = useCallback(
    async (jobId) => {
      const jobData = await fetchJobStatus(jobId);
      setActiveJob((prev) => {
        if (!prev) {
          return jobData;
        }
        const merged = {
          ...prev,
          ...jobData,
        };
        if (!jobData || !jobData.jobId) {
          return prev;
        }
        if (!jobData.status || jobData.status === "UNKNOWN") {
          merged.status = prev.status;
        }
        if (!jobData.lastStep) {
          merged.lastStep = prev.lastStep;
        }
        if (
          typeof jobData.totalFiles !== "number" ||
          Number.isNaN(jobData.totalFiles)
        ) {
          merged.totalFiles = prev.totalFiles;
        }
        if (
          typeof jobData.processedFiles !== "number" ||
          Number.isNaN(jobData.processedFiles)
        ) {
          merged.processedFiles = prev.processedFiles;
        }
        if (jobData.metadata == null && prev.metadata) {
          merged.metadata = prev.metadata;
        }
        return merged;
      });
      return jobData;
    },
    [fetchJobStatus]
  );

  const handleRefreshJob = useCallback(async () => {
    if (!activeJob?.jobId) {
      return;
    }
    try {
      await updateJobFromServer(activeJob.jobId);
      setJobPollError("");
    } catch (error) {
      setJobPollError(
        error?.response?.data?.error ??
          error?.message ??
          "Impossibile aggiornare lo stato del job."
      );
    }
  }, [activeJob?.jobId, updateJobFromServer]);

  const handleApproveJob = useCallback(async () => {
    if (!activeJob?.jobId) {
      return;
    }
    setJobActionStatus("approve");
    setJobActionError("");
    try {
      await axios.post(
        `${apiUrl}/moda/articoli/${encodeURIComponent(codiceVS)}/immagini/${
          activeJob.jobId
        }/approve`,
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );
      await updateJobFromServer(activeJob.jobId);
      setJobPollError("");
    } catch (error) {
      setJobActionError(
        error?.response?.data?.error ??
          error?.message ??
          "Errore durante l'approvazione delle immagini."
      );
    } finally {
      setJobActionStatus("idle");
    }
  }, [activeJob?.jobId, apiUrl, codiceVS, token, updateJobFromServer]);

  const handleRejectJob = useCallback(async () => {
    if (!activeJob?.jobId) {
      return;
    }
    setJobActionStatus("reject");
    setJobActionError("");
    try {
      await axios.post(
        `${apiUrl}/moda/articoli/${encodeURIComponent(codiceVS)}/immagini/${
          activeJob.jobId
        }/reject`,
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );
      await updateJobFromServer(activeJob.jobId);
      setJobPollError("");
    } catch (error) {
      setJobActionError(
        error?.response?.data?.error ??
          error?.message ??
          "Errore durante il rifiuto delle immagini."
      );
    } finally {
      setJobActionStatus("idle");
    }
  }, [activeJob?.jobId, apiUrl, codiceVS, token, updateJobFromServer]);

  const handleReportImageNotFound = useCallback(async () => {
    if (!codiceVS) {
      setMissingImageStatus("error");
      setMissingImageMessage("Codice VS mancante.");
      return;
    }
    setMissingImageStatus("pending");
    setMissingImageMessage("");
    try {
      await axios.post(
        `${apiUrl}/moda/articoli/${encodeURIComponent(
          codiceVS
        )}/immagini/not-found`,
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );
      setMissingImageStatus("success");
      setMissingImageMessage(
        "Segnalazione inviata. Il prodotto è stato liberato dalla prenotazione immagini."
      );
      if (typeof onCompleted === "function") {
        onCompleted({ action: "image-not-found" });
      }
    } catch (error) {
      setMissingImageStatus("error");
      setMissingImageMessage(
        error?.response?.data?.error ??
          error?.message ??
          "Errore durante la segnalazione."
      );
    }
  }, [apiUrl, codiceVS, token, onCompleted]);


  const handleLibraryDeliveryScheduled = useCallback((jobId) => {
    if (!jobId) return;
    setActiveJob({
      jobId,
      productCode: codiceVS,
      brandCode: modaBrandCode,
      status: "PENDING",
      totalFiles: 0,
      processedFiles: 0,
      lastStep: "Reinvio revisione R2 accodato",
      message: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      metadata: null,
    });
    setJobPollError("");
    setJobActionError("");
    if (typeof onCompleted === "function") {
      onCompleted({ action: "images-library-delivery", jobId });
    }
  }, [codiceVS, modaBrandCode, onCompleted]);

  const handleReportImageExists = useCallback(async () => {
    if (!codiceVS) {
      setExistingImageStatus("error");
      setExistingImageMessage("Codice VS mancante.");
      return;
    }
    setExistingImageStatus("pending");
    setExistingImageMessage("");
    try {
      await axios.post(
        `${apiUrl}/moda/articoli/${encodeURIComponent(
          codiceVS
        )}/immagini/already-present`,
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );
      setExistingImageStatus("success");
      setExistingImageMessage(
        "Segnalazione registrata. Il prodotto è stato liberato ed è segnato come con immagine."
      );
      if (typeof onCompleted === "function") {
        onCompleted({ action: "image-present" });
      }
    } catch (error) {
      setExistingImageStatus("error");
      setExistingImageMessage(
        error?.response?.data?.error ??
          error?.message ??
          "Errore durante l'aggiornamento."
      );
    }
  }, [apiUrl, codiceVS, token, onCompleted]);

  useEffect(() => {
    if (!activeJob?.jobId) {
      return;
    }
    const status = activeJob.status ?? "";
    if (!JOB_ACTIVE_STATUSES.has(status)) {
      return;
    }
    let isCancelled = false;

    const poll = async () => {
      try {
        await updateJobFromServer(activeJob.jobId);
        if (!isCancelled) {
          setJobPollError("");
        }
      } catch (error) {
        if (!isCancelled) {
          setJobPollError(
            error?.response?.data?.error ??
              error?.message ??
              "Errore durante il recupero dello stato del job."
          );
        }
      }
    };

    poll();
    const interval = window.setInterval(poll, JOB_POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(interval);
    };
  }, [activeJob?.jobId, activeJob?.status, updateJobFromServer]);

  useEffect(() => {
    if (!metadataKey) {
      if (loadedPreviewKey !== null) {
        setLoadedPreviewKey(null);
      }
      setJobPreviews((prev) => {
        revokePreviewUrls(prev);
        return [];
      });
      jobPreviewsRef.current = [];
      return;
    }
    if (loadedPreviewKey === metadataKey) {
      return;
    }

    let isSubscribed = true;
    const files = activeJob?.metadata?.files ?? [];

    const loadPreviews = async () => {
      try {
        const results = await Promise.all(
          files.map(async (info, index) => {
            try {
              const response = await axios.get(
                `${apiUrl}/moda/articoli/${encodeURIComponent(
                  codiceVS
                )}/immagini/${activeJob.jobId}/preview/${index}`,
                {
                  headers: {
                    Authorization: token ? `Bearer ${token}` : undefined,
                  },
                  responseType: "blob",
                }
              );
              const url = URL.createObjectURL(response.data);
              return { index, url, info, error: null };
            } catch (error) {
              return {
                index,
                url: null,
                info,
                error:
                  error?.response?.data?.error ??
                  error?.message ??
                  "Errore durante il caricamento dell'anteprima.",
              };
            }
          })
        );

        if (!isSubscribed) {
          results.forEach((item) => {
            if (item?.url) {
              URL.revokeObjectURL(item.url);
            }
          });
          return;
        }

        setJobPreviews((prev) => {
          revokePreviewUrls(prev);
          return results;
        });
        jobPreviewsRef.current = results;
        setLoadedPreviewKey(metadataKey);
      } catch (error) {
        if (!isSubscribed) {
          return;
        }
        const fallback = files.map((info, index) => ({
          index,
          url: null,
          info,
          error:
            error?.response?.data?.error ??
            error?.message ??
            "Errore durante il caricamento delle anteprime.",
        }));
        setJobPreviews((prev) => {
          revokePreviewUrls(prev);
          return fallback;
        });
        jobPreviewsRef.current = fallback;
        setLoadedPreviewKey(metadataKey);
      }
    };

    loadPreviews();

    return () => {
      isSubscribed = false;
      revokePreviewUrls(jobPreviewsRef.current);
    };
  }, [
    metadataKey,
    loadedPreviewKey,
    activeJob?.metadata,
    activeJob?.jobId,
    apiUrl,
    codiceVS,
    token,
    revokePreviewUrls,
  ]);

  useEffect(() => {
    const status = activeJob?.status ?? "";
    if (status && !JOB_ACTIVE_STATUSES.has(status)) {
      setJobPollError("");
    }
  }, [activeJob?.status]);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active?.id ?? null);
    setSuccessMessage("");
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      setActiveId(null);
      if (!over || active.id === over.id) {
        return;
      }
      setItems((prev) => {
        const oldIndex = prev.findIndex((item) => item.id === active.id);
        const newIndex = prev.findIndex((item) => item.id === over.id);
        if (oldIndex === -1 || newIndex === -1) {
          return prev;
        }
        const reordered = arrayMove(prev, oldIndex, newIndex).map(
          (item, index) => ({
            ...item,
            targetName: buildTargetFileName(item.originalName, index, codiceVS),
          })
        );
        return reordered;
      });
      setUploadStatus("idle");
      setErrorMessage("");
      setSuccessMessage("Ordine immagini aggiornato.");
    },
    [codiceVS]
  );

  const handleUpload = useCallback(async () => {
    if (!hasItemsSelected || !codiceVS || !token || !modaBrandCode) {
      return;
    }
    if (!canStartNewUpload) {
      setErrorMessage(
        jobDisableUploadsReason ||
          "Completa la revisione del job corrente prima di un nuovo upload."
      );
      return;
    }
    const filesCount = items.length;
    setUploadStatus("uploading");
    setErrorMessage("");
    setSuccessMessage("");
    setProgress(0);
    const formData = new FormData();
    formData.append("productCode", codiceVS);
    formData.append("brandCode", modaBrandCode);
    if (uploadedBy) {
      formData.append("uploadedBy", uploadedBy);
    }
    items.forEach((item) => {
      formData.append("images", item.file, item.targetName);
    });
    try {
      const response = await axios.post(
        `${apiUrl}/moda/articoli/${encodeURIComponent(codiceVS)}/immagini`,
        formData,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (evt) => {
            if (evt.total) {
              const percentage = Math.round((evt.loaded * 100) / evt.total);
              setProgress(percentage);
            }
          },
        }
      );
      const responseData = response?.data ?? {};
      const jobIdResponse =
        responseData.jobId ??
        responseData.job_id ??
        responseData?.data?.jobId ??
        null;
      if (!jobIdResponse) {
        throw new Error("Il server non ha restituito un jobId valido.");
      }
      setUploadStatus("success");
      setSuccessMessage(
        `Immagini caricate. Job ${jobIdResponse} in elaborazione.`
      );
      setProgress(100);
      setActiveJob({
        jobId: jobIdResponse,
        productCode: codiceVS,
        brandCode: modaBrandCode,
        status: "PENDING",
        totalFiles: filesCount,
        processedFiles: 0,
        lastStep: "Job inviato per elaborazione",
        message: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        metadata: null,
      });
      setJobPollError("");
      setJobActionError("");
      setLoadedPreviewKey(null);
      setJobPreviews((prev) => {
        revokePreviewUrls(prev);
        return [];
      });
      clearItems();
      if (typeof onCompleted === "function") {
        onCompleted({ action: "images-uploaded", jobId: jobIdResponse });
      }
    } catch (error) {
      console.error("Errore upload immagini", error);
      setUploadStatus("error");
      setErrorMessage(
        error?.response?.data?.error ??
          error?.response?.data?.message ??
          error?.message ??
          "Non e' stato possibile caricare le immagini."
      );
    }
  }, [
    items,
    hasItemsSelected,
    codiceVS,
    token,
    modaBrandCode,
    canStartNewUpload,
    jobDisableUploadsReason,
    uploadedBy,
    clearItems,
    revokePreviewUrls,
    onCompleted,
  ]);

  return (
    <div className="modificaProdottiImmaginiLayout">
      <aside className="modificaProdottiImmaginiAside pa3 bl-l bordoColAlt">
        <h3 className="mt0 mb3">Immagini prodotto</h3>
        <div className="modificaProdottiImmaginiOnline mb3">
          <section className="ba bordoColAlt br2 pa2 tc">
            <h4 className="mt0 mb2">Viglietti</h4>
            {codicePS ? (
              <ImmaginiArticolo
                prodIDps={codicePS}
                size="home_default"
                fonte="VIG"
                refreshKey={refreshKey}
                zoomable
                zoomSize="original"
                imageClassName="modificaProdottiImmaginiThumb br2"
              />
            ) : (
              <p className="f7 gray ma0">ID Prestashop non disponibile.</p>
            )}
          </section>
          <section className="ba bordoColAlt br2 pa2 tc">
            <h4 className="mt0 mb2">Bottero</h4>
            {botProdIDps ? (
              <ImmaginiArticolo
                prodIDps={botProdIDps}
                size="home_default"
                fonte="BOT"
                refreshKey={refreshKey}
                zoomable
                zoomSize="original"
                imageClassName="modificaProdottiImmaginiThumb br2"
              />
            ) : (
              <p className="f7 gray ma0">ID Prestashop non disponibile.</p>
            )}
          </section>
        </div>
        <h4 className="mt0 mb2">Archivio Domino R2</h4>
        <LibreriaImmaginiProdotto
          codiceVS={codiceVS}
          token={token}
          updatedBy={uploadedBy}
          brandCode={modaBrandCode}
          onDeliveryScheduled={handleLibraryDeliveryScheduled}
          refreshKey={`${refreshKey || ""}:${activeJob?.metadata?.archive?.revisionId || ""}`}
          fallback={
            <p className="f7 gray mt0 mb3">
              Nessuna immagine archiviata in R2 per questo prodotto.
            </p>
          }
        />
        <div>
          <p className="ma0 mb2 f6">
            L&apos;immagine è già presente? Segnalala e libera
            la prenotazione immagini.
          </p>
          <button
            type="button"
            className={buttonClassesGreen}
            onClick={handleReportImageExists}
            disabled={
              !codiceVS ||
              !token ||
              existingImageStatus === "pending" ||
              jobActionInProgress
            }
          >
            C&apos;è già
          </button>
          {existingImageMessage && (
            <p
              className={`mt2 mb0 f6 ${
                existingImageStatus === "error" ? "dark-red" : "dark-green"
              }`}
            >
              {existingImageMessage}
            </p>
          )}
        </div>
      </aside>
      <div className="modificaProdottiImmaginiMain sfondoBiancoTema pa3 mb3">
        <h3 className="mt0 mb3">Caricamento immagini prodotto</h3>
      <div className="mb3">
        <label className="db mb2 fw6" htmlFor="brandOverrideInput">
          Forza brand code Moda (opzionale)
        </label>
        <input
          id="brandOverrideInput"
          type="text"
          className="input-reset ba bordoColScrittaDef-10 br2 pa2 w-100 measure"
          placeholder="Inserisci brand code Moda manualmente"
          value={brandOverride}
          onChange={(event) => setBrandOverride(event.target.value)}
          disabled={uploadStatus === "uploading"}
        />
        <small className="db mt1 gray">
          Se compilato, questo valore verr&agrave; usato al posto del brand code
          ricevuto da Moda.
        </small>
      </div>
      {activeJob && (
        <div className="mb3 pa3 br3 bg-near-white">
          <div className="flex items-center justify-between">
            <h4 className="ma0">Stato job immagini</h4>
            <span
              className={`ttu f6 fw6 ${getStatusClassName(activeJob.status)}`}
              title={jobErrorTooltip ?? undefined}
            >
              {activeJob.status ?? "N/D"}
            </span>
          </div>
          {(showDebugDetails || !jobAwaitingReview) && (
            <p className="mt2 mb1 f6">
              Job ID:&nbsp;
              <code>{activeJob.jobId}</code>
            </p>
          )}
          {jobTimeline.length > 0 && (
            <div className="mt3">
              <div className="flex flex-column">
                {jobTimeline.map((step, index) => {
                  const errorTooltip =
                    step.state === "error"
                      ? jobErrorTooltip ?? "Errore durante questa fase."
                      : undefined;
                  return (
                    <div key={step.key} className="flex">
                      <div className="flex flex-column items-center mr3">
                        <span
                          className={getTimelineDotClasses(step.state)}
                          title={errorTooltip}
                        />
                        {index < jobTimeline.length - 1 && (
                          <div
                            className={`br-pill ${getTimelineConnectorClasses(
                              step.state
                            )}`}
                            style={{
                              width: "0.25rem",
                              flexGrow: 1,
                              minHeight: "2.5rem",
                            }}
                          />
                        )}
                      </div>
                      <div className="pb3">
                        <p
                          className={`ma0 f6 ${getTimelineLabelClasses(
                            step.state
                          )}`}
                          title={errorTooltip}
                        >
                          {step.label}
                        </p>
                        <span
                          className={`${getTimelineBadgeClasses(
                            step.state
                          )} f7 dib mt1`}
                        >
                          {TIMELINE_STATE_LABELS[step.state] ?? step.state}
                        </span>
                        {step.detail && (
                          <p className="mt1 mb0 f7 gray">{step.detail}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {activeJob.lastStep && (
            <p className="mt2 mb1 f6">
              Ultimo step:&nbsp;
              <span className="fw6">{activeJob.lastStep}</span>
            </p>
          )}
          {activeJob.totalFiles ? (
            <p className="mt1 mb2 f6">
              Avanzamento rielaborazione:&nbsp;
              {numberFormatter.format(activeJob.processedFiles ?? 0)}/
              {numberFormatter.format(activeJob.totalFiles)} (
              {jobProgressPercent}%)
            </p>
          ) : null}
          {JOB_ACTIVE_STATUSES.has(activeJob.status ?? "") && (
            <div className="w-100 bg-light-gray br-pill overflow-hidden h1 mb2">
              <div
                className={`h1 br-pill ${progressBarClass}`}
                style={{
                  width: `${jobProgressPercent}%`,
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          )}
          {jobAwaitingReview && (
            <p className="mt1 mb2 dark-blue f6">
              Immagini pronte per revisione e approvazione.
            </p>
          )}
          {activeJob.message && (
            <p
              className={`mt1 mb2 f6 ${
                activeJob.status === "ERROR"
                  ? "dark-red"
                  : activeJob.status === "REJECTED"
                  ? "dark-red"
                  : activeJob.status === "SUCCESS"
                  ? "dark-green"
                  : ""
              }`}
            >
              {activeJob.message}
            </p>
          )}
          {jobPollError && (
            <div className="mt2 dark-red f6">
              <strong>Errore polling:</strong> {jobPollError}
            </div>
          )}
          <div className="flex items-center mt2">
            <button
              type="button"
              className={buttonClassesWhite}
              onClick={handleRefreshJob}
              disabled={!activeJob.jobId || jobActionInProgress}
            >
              Aggiorna stato
            </button>
            {!canStartNewUpload && (
              <span className="ml3 gray f6">{jobDisableUploadsReason}</span>
            )}
          </div>
          {jobActionError && !jobAwaitingReview && (
            <div className="mt3 dark-red f6">
              <strong>Errore:</strong> {jobActionError}
            </div>
          )}
          {jobIsTerminal &&
            activeJob.status === "SUCCESS" &&
            activeJob.metadata && (
              <div className="mt3">
                <h5 className="ma0 f6 ttu tracked gray">Dettagli pipeline</h5>
                {activeJob.metadata.stage && (
                  <p className="mt2 mb1 f6">
                    Stage finale:&nbsp;
                    <code>{activeJob.metadata.stage}</code>
                  </p>
                )}
                {activeJob.metadata.connector && (
                  <div className="mt2 pa3 br2 ba b--black-10 bg-washed-blue">
                    <p className="ma0 fw6">Connector</p>
                    <pre className="mt2 mb0 f7 code bg-near-white ba b--black-05 br2 pa2 overflow-auto">
                      {JSON.stringify(activeJob.metadata.connector, null, 2)}
                    </pre>
                  </div>
                )}
                {activeJob.metadata.connectorCopy && (
                  <div className="mt2 pa3 br2 ba b--black-10 bg-washed-green">
                    <p className="ma0 fw6">Copia locale</p>
                    <pre className="mt2 mb0 f7 code bg-near-white ba b--black-05 br2 pa2 overflow-auto">
                      {JSON.stringify(
                        activeJob.metadata.connectorCopy,
                        null,
                        2
                      )}
                    </pre>
                  </div>
                )}
                {activeJob.metadata.cleanup && (
                  <div className="mt2 pa3 br2 ba b--black-10 bg-washed-yellow">
                    <p className="ma0 fw6">Cleanup</p>
                    <pre className="mt2 mb0 f7 code bg-near-white ba b--black-05 br2 pa2 overflow-auto">
                      {JSON.stringify(activeJob.metadata.cleanup, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          {jobIsTerminal &&
            activeJob.status === "ERROR" &&
            activeJob.metadata && (
              <div className="mt3 pa3 br2 ba b--light-red bg-washed-red">
                {activeJob.metadata.stage && (
                  <p className="ma0 f6">
                    Stage fallito:&nbsp;
                    <code>{activeJob.metadata.stage}</code>
                  </p>
                )}
                {activeJob.metadata.error?.message && (
                  <p className="mt2 mb1 f6 fw6 dark-red">
                    {activeJob.metadata.error.message}
                  </p>
                )}
                {activeJob.metadata.error && (
                  <pre className="mt2 mb0 f7 code bg-near-white ba b--black-05 br2 pa2 overflow-auto">
                    {JSON.stringify(activeJob.metadata.error, null, 2)}
                  </pre>
                )}
              </div>
            )}
          {jobIsTerminal && (
            <div className="mt3">
              <button
                type="button"
                className={buttonClassesWhite}
                onClick={resetJobState}
                disabled={jobActionInProgress}
              >
                Nuovo caricamento
              </button>
            </div>
          )}
        </div>
      )}
      {jobAwaitingReview && activeJob?.metadata && (
        <div className="mb3">
          <h4 className="mt0 mb2">Anteprime immagini elaborate</h4>
          {showDebugDetails && (
            <p className="mv1 f6">
              Cartella temporanea:&nbsp;
              <code>{activeJob.metadata.tempDir}</code>
            </p>
          )}
          {showDebugDetails && activeJob.metadata.target && (
            <p className="mv1 f6">
              Target:&nbsp;
              {typeof activeJob.metadata.target.width === "number" &&
              typeof activeJob.metadata.target.height === "number"
                ? `${numberFormatter.format(
                    activeJob.metadata.target.width
                  )}x${numberFormatter.format(
                    activeJob.metadata.target.height
                  )} px`
                : "N/D"}{" "}
              @{" "}
              {typeof activeJob.metadata.target.dpi === "number"
                ? `${activeJob.metadata.target.dpi} DPI`
                : "N/D"}
            </p>
          )}
          {activeJob.metadata.createdAt && (
            <p className="mv1 f6">
              Elaborazione completata:&nbsp;
              {new Date(activeJob.metadata.createdAt).toLocaleString("it-IT")}
            </p>
          )}
          {metadataFilesCount === 0 ? (
            <p className="mv2 gray">Nessuna immagine elaborata.</p>
          ) : jobPreviews.length === 0 ? (
            <p className="mv2 gray">Caricamento anteprime in corso...</p>
          ) : null}
          {jobPreviews.map((preview) => (
            <div
              key={preview.index}
              className="ba b--black-10 br3 pa3 mb3 flex flex-column flex-row-ns"
            >
              <div className="w-100 w-40-ns mr0 mr3-ns mb3 mb0-ns">
                {preview.url ? (
                  <img
                    src={preview.url}
                    alt={
                      preview.info?.processedFilename ??
                      `Immagine ${preview.index + 1}`
                    }
                    className="w-100 br2 ba b--black-10 bg-black-05"
                    style={{
                      objectFit: "contain",
                      width: "100%",
                      height: "auto",
                      maxHeight: "24rem",
                    }}
                  />
                ) : (
                  <div className="pa3 tc bg-washed-red br2 f6">
                    {preview.error ?? "Anteprima non disponibile"}
                  </div>
                )}
              </div>
              <div className="flex-auto f6">
                <p className="ma0 fw6">
                  Immagine {numberFormatter.format(preview.index + 1)}
                </p>
                <p className="ma0 mt2">
                  <strong>File elaborato:</strong>{" "}
                  {preview.info?.processedFilename ?? "N/D"}
                </p>
                <p className="ma0 mt2">
                  <strong>Origine:</strong>{" "}
                  {preview.info?.originalName ?? "N/D"}
                </p>
                <p className="ma0 mt2">
                  <strong>Dimensioni originali:</strong>{" "}
                  {preview.info?.originalWidth && preview.info?.originalHeight
                    ? `${numberFormatter.format(
                        preview.info.originalWidth
                      )}x${numberFormatter.format(
                        preview.info.originalHeight
                      )} px`
                    : "N/D"}
                </p>
                <p className="ma0 mt2">
                  <strong>Dimensioni finali:</strong>{" "}
                  {preview.info?.processedWidth && preview.info?.processedHeight
                    ? `${numberFormatter.format(
                        preview.info.processedWidth
                      )}x${numberFormatter.format(
                        preview.info.processedHeight
                      )} px`
                    : "N/D"}
                </p>
                <p className="ma0 mt2">
                  <strong>Dimensione originale:</strong>{" "}
                  {preview.info?.originalSize
                    ? `${numberFormatter.format(
                        Math.round(preview.info.originalSize / 1024)
                      )} KB`
                    : "N/D"}
                </p>
                <p className="ma0 mt2">
                  <strong>Dimensione file:</strong>{" "}
                  {preview.info?.processedSize
                    ? `${numberFormatter.format(
                        Math.round(preview.info.processedSize / 1024)
                      )} KB`
                    : "N/D"}
                </p>
                {preview.info?.warnings?.length ? (
                  <div className="mt2 pa2 br2 bg-washed-yellow">
                    <strong>Warning:</strong>
                    <ul className="mt1 mb0 pl3">
                      {preview.info.warnings.map((warning, idx) => (
                        <li key={`${preview.index}-warning-${idx}`}>
                          {warning}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
          <div className="mt3 flex flex-column flex-row-ns">
            <button
              type="button"
              className={`${buttonClassesGreen} mr0 mr3-ns mb2 mb0-ns`}
              onClick={handleApproveJob}
              disabled={jobActionInProgress}
            >
              Approva immagini
            </button>
            <button
              type="button"
              className={buttonClassesRed}
              onClick={handleRejectJob}
              disabled={jobActionInProgress}
            >
              Rifiuta
            </button>
          </div>
          {jobActionInProgress && (
            <p className="mt2 gray f6">
              {jobActionStatus === "approve"
                ? "Invio approvazione..."
                : "Invio rifiuto..."}
            </p>
          )}
          {jobActionError && (
            <div className="mt3 dark-red f6">
              <strong>Errore:</strong> {jobActionError}
            </div>
          )}
        </div>
      )}
      <div className="mb3">
        <label className={selectButtonClass}>
          <input
            ref={fileInputRef}
            className="dn"
            type="file"
            accept="image/jpeg,image/png"
            multiple
            onChange={handleFileSelection}
            disabled={!canSelectImages}
          />
          Seleziona immagini
        </label>
        {!canSelectImages && (
          <span className="ml3 dark-red">
            {jobIsActive
              ? jobDisableUploadsReason
              : "Inserire codice VS, impostare il brand code Moda e attendere il token prima di caricare."}
          </span>
        )}
      </div>
      <div
        className={[
          "ba",
          "br3",
          "pa4",
          "tc",
          "mb3",
          "pointer",
          "flex",
          "flex-column",
          "items-center",
          isDragActive ? "bg-washed-blue" : "bg-near-white",
          hasValidContext ? "" : "o-50 not-allowed pointer-events-none",
        ].join(" ")}
        style={{ borderStyle: "dashed" }}
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={triggerFileDialog}
        role="button"
        tabIndex={hasValidContext ? 0 : -1}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            triggerFileDialog();
          }
        }}
      >
        <span className="material-icons mb2">cloud_upload</span>
        <span className="fw6">
          Trascina qui le immagini oppure usa il pulsante "Seleziona immagini"
        </span>
        <span className="f6 mt2 gray">
          Formati supportati: JPG, JPEG, PNG. Max 20mb / {MAX_FILES} file per
          upload.
        </span>
      </div>
      {items.length > 0 && (
        <div className="mb3">
          <div className="overflow-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext
                items={items.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <table className="collapse ba br2 b--light-silver pv2 ph3 w-100">
                  <thead>
                    <tr className="striped--near-white">
                      <th className="pv2 ph3 tl w3">Ordine</th>
                      <th className="pv2 ph3 tl">Anteprima</th>
                      <th className="pv2 ph3 tl">Nome originale</th>
                      <th className="pv2 ph3 tl">Nuovo nome</th>
                      <th className="pv2 ph3 tl">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <SortableRow
                        key={item.id}
                        item={item}
                        isActive={activeId === item.id}
                        onRemove={handleRemoveItem}
                        buttonClassesRed={buttonClassesRed}
                      />
                    ))}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          </div>
        </div>
      )}
      {uploadStatus === "uploading" && (
        <div className="mb3">
          <div className="w-100 bg-light-gray br-pill overflow-hidden h1">
            <div
              className="bg-green h1 br-pill"
              style={{ width: `${progress}%`, transition: "width 0.3s ease" }}
            />
          </div>
          <p className="mt2 mb0">Caricamento in corso: {progress}%</p>
        </div>
      )}
      {errorMessage && (
        <div className="mb3 dark-red">
          <strong>Errore:</strong> {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="mb3 dark-green">
          <strong>OK:</strong> {successMessage}
        </div>
      )}
      <div className="flex items-center">
        <button
          className={uploadButtonClass}
          onClick={handleUpload}
          disabled={
            !hasItemsSelected ||
            !hasValidContext ||
            uploadStatus === "uploading"
          }
        >
          Carica immagini
        </button>
        {items.length > 0 && (
          <button
            className={`${buttonClassesWhite} ml2`}
            onClick={handleClearSelection}
            disabled={uploadStatus === "uploading"}
          >
            Svuota selezione
          </button>
        )}
      </div>

      <div className="mt3 pa3 br3 bg-washed-red">
        <p className="ma0 mb2 fw6">Non trovi l'immagine sul sito del brand?</p>
        <p className="mt0 mb2 f6">
          Premi il tasto qui sotto per liberare la prenotazione immagini e
          segnalare che l'immagine non è reperibile online.
        </p>
        <button
          type="button"
          className={buttonClassesRed}
          onClick={handleReportImageNotFound}
          disabled={
            !codiceVS ||
            !token ||
            missingImageStatus === "pending" ||
            jobActionInProgress
          }
        >
          {missingImageStatus === "pending"
            ? "Invio segnalazione..."
            : "Segnala immagine non trovata"}
        </button>
        {missingImageMessage && (
          <p
            className={`mt2 mb0 f6 ${
              missingImageStatus === "error" ? "dark-red" : "dark-green"
            }`}
          >
            {missingImageMessage}
          </p>
        )}
      </div>
      </div>
    </div>
  );
}

function SortableRow({ item, onRemove, buttonClassesRed, isActive }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const highlightClass = isDragging || isActive ? "bg-washed-green" : "";

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`striped--light-gray ${highlightClass}`}
    >
      <td className="pv2 ph3 w2">
        <button
          type="button"
          className="bn bg-transparent pointer flex items-center justify-center gray"
          {...attributes}
          {...listeners}
        >
          <span className="material-icons">drag_indicator</span>
        </button>
      </td>
      <td className="pv2 ph3">
        <div
          className="ba b--light-silver br2 flex items-center justify-center bg-near-white"
          style={{ width: "5rem", height: "5rem" }}
        >
          <img
            src={item.previewUrl}
            alt={item.targetName}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </div>
      </td>
      <td className="pv2 ph3">{item.originalName}</td>
      <td className="pv2 ph3">{item.targetName}</td>
      <td className="pv2 ph3">
        <button
          className={buttonClassesRed}
          type="button"
          onClick={() => onRemove(item.id)}
        >
          Rimuovi
        </button>
      </td>
    </tr>
  );
}

export default UploadImmaginiArticolo;
